/**
 * Hook for handling generic payment redirect returns
 */
import { useEffect } from 'react';
export function useGenericPaymentReturn({ startPolling, handlePaymentAction, setIsLoading, setError, setCurrentPaymentId, hookOptionsRef, redirectReturnProcessedRef, }) {
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        if (redirectReturnProcessedRef.current)
            return;
        const urlParams = new URLSearchParams(window.location.search);
        const paymentAction = urlParams.get('paymentAction');
        const paymentActionStatus = urlParams.get('paymentActionStatus');
        const paymentIdFromUrl = urlParams.get('paymentId');
        const paymentMode = urlParams.get('mode');
        // Skip if in retrieve mode
        if (paymentMode === 'retrieve') {
            console.log('⏭️ [usePayment] Skipping - retrieve mode detected (handled by usePaymentRetrieve)');
            return;
        }
        // Check if returning from a payment redirect
        if (paymentAction !== 'requireAction' || paymentActionStatus !== 'completed' || !paymentIdFromUrl) {
            console.log('⏭️ [usePayment] No payment redirect detected - normal page load');
            return;
        }
        console.log('✅ [usePayment] Payment redirect return detected! Starting auto-polling...', {
            paymentId: paymentIdFromUrl,
        });
        redirectReturnProcessedRef.current = true;
        setIsLoading(true);
        setCurrentPaymentId(paymentIdFromUrl);
        // Start polling for the payment status
        startPolling(paymentIdFromUrl, {
            onRequireAction: (payment) => {
                console.log('⚠️ [usePayment] Payment requires new action', payment);
                void handlePaymentAction(payment, {});
            },
            onSuccess: async (payment) => {
                console.log('✅ [usePayment] Payment succeeded after redirect!', {
                    paymentId: payment.id,
                    status: payment.status,
                    hasOrder: !!payment.order,
                });
                setIsLoading(false);
                // Clean up ONLY payment-related query parameters (preserve funnel/checkout params)
                const urlParams = new URLSearchParams(window.location.search);
                const paymentParams = ['paymentAction', 'paymentActionStatus', 'paymentId', 'payment_intent', 'payment_intent_client_secret', 'source_type', 'redirect_status'];
                paymentParams.forEach(param => urlParams.delete(param));
                const newUrl = urlParams.toString()
                    ? `${window.location.pathname}?${urlParams.toString()}`
                    : window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
                console.log('🧹 [usePayment] Payment URL parameters cleaned up (preserved funnel/checkout params)');
                // Call hook-level onPaymentCompleted callback (if provided)
                if (hookOptionsRef.current?.onPaymentCompleted) {
                    console.log('📞 [usePayment] Calling onPaymentCompleted callback...', {
                        isRedirectReturn: true,
                    });
                    try {
                        await hookOptionsRef.current.onPaymentCompleted(payment, {
                            isRedirectReturn: true,
                            order: payment.order,
                        });
                        console.log('✅ [usePayment] onPaymentCompleted callback completed successfully');
                    }
                    catch (error) {
                        console.error('❌ [usePayment] Error in onPaymentCompleted callback:', error);
                    }
                }
                else {
                    console.warn('⚠️ [usePayment] No onPaymentCompleted callback provided');
                }
            },
            onFailure: async (errorMsg) => {
                console.error('❌ [usePayment] Payment failed after redirect:', errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                // Clean up ONLY payment-related query parameters (preserve funnel/checkout params)
                const urlParams = new URLSearchParams(window.location.search);
                const paymentParams = ['paymentAction', 'paymentActionStatus', 'paymentId', 'payment_intent', 'payment_intent_client_secret', 'source_type', 'redirect_status'];
                paymentParams.forEach(param => urlParams.delete(param));
                const newUrl = urlParams.toString()
                    ? `${window.location.pathname}?${urlParams.toString()}`
                    : window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
                // Call hook-level onPaymentFailed callback (if provided)
                if (hookOptionsRef.current?.onPaymentFailed) {
                    console.log('📞 [usePayment] Calling onPaymentFailed callback...');
                    try {
                        await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                            isRedirectReturn: true,
                        });
                    }
                    catch (error) {
                        console.error('❌ [usePayment] Error in onPaymentFailed callback:', error);
                    }
                }
            },
        });
    }, [startPolling, handlePaymentAction, setIsLoading, setError, setCurrentPaymentId, hookOptionsRef, redirectReturnProcessedRef]);
}
